import React, { useCallback, useState } from "react";
import { useDocumentTemplates } from "../../../hooks/useDocumentTemplates";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import {
  Stack,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Button,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Chip,
  Modal,
  ModalDialog,
  DialogTitle,
} from "@mui/joy";
import { useDropzone } from "react-dropzone";
import Loading from "../../../../../components/Loading";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";

interface TemplatesTabProps {
  documentTypeId?: number;
  workspace_id: string;
  onChange: () => void;
}

const TemplatesTab: React.FC<TemplatesTabProps> = ({
  documentTypeId,
  workspace_id,
  onChange,
}) => {
  const [styleGuideOpen, setStyleGuideOpen] = useState(false);
  const [errorModal, setErrorModal] = useState<{
    isOpen: boolean;
    message: string;
  }>({
    isOpen: false,
    message: "",
  });

  const {
    templates,
    isLoading,
    error,
    refreshTemplates,
    deleteTemplate,
    deletingId,
  } = useDocumentTemplates(documentTypeId, workspace_id);

  const authFetch = useAuthFetch();

  const getTemplatesByType = useCallback(
    (type: string) => {
      const filtered = templates.filter(
        (template) => template.templateType === type,
      );
      return filtered;
    },
    [templates],
  );

  // Show error modal when error state changes
  React.useEffect(() => {
    if (error) {
      setErrorModal({ isOpen: true, message: error.message });
    }
  }, [error]);

  const uploadFile = useCallback(
    async (file: File, templateType: string) => {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("documentType", templateType);
        formData.append("template_type", templateType);

        // Construct URL with workspace parameters
        const url = `/api/workspace/meridian/admin/document_types/${documentTypeId}/templates?workspace_id=${workspace_id}`;

        await authFetch(
          url,
          "create document type template",
          {
            method: "POST",
            body: formData,
          },
          120000,
        ); //

        refreshTemplates();
        onChange();
      } catch (err: any) {
        console.error("Upload failed:", err);
        throw err;
      }
    },
    [authFetch, documentTypeId, onChange, refreshTemplates, workspace_id],
  );

  if (!documentTypeId) {
    return (
      <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
        Please save basic information first before managing templates.
      </Box>
    );
  }

  if (isLoading) {
    return <Loading>Loading Templates...</Loading>;
  }

  const FileUploadSection = ({
    number,
    title,
    icon: Icon,
    tooltip,
    templateType,
    maxFiles,
    maxSize,
    accept,
  }: {
    number: string;
    title: string;
    icon: React.ElementType;
    tooltip: string;
    templateType: string;
    maxFiles: number;
    maxSize: number;
    accept: Record<string, string[]>;
  }) => {
    const [files, setFiles] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const existingFiles = getTemplatesByType(templateType);

    const { getRootProps, getInputProps } = useDropzone({
      onDrop: async (acceptedFiles) => {
        if (uploading) return;
        if (acceptedFiles.length + existingFiles.length > maxFiles) {
          setError(`Maximum ${maxFiles} files allowed`);
          return;
        }

        setFiles(acceptedFiles);
        setError(null);
        setUploading(true);

        try {
          for (const file of acceptedFiles) {
            await uploadFile(file, templateType);
          }
        } catch (err: any) {
          setError(err.message);
        } finally {
          setUploading(false);
        }
      },
      accept,
      maxSize,
      multiple: maxFiles > 1,
      disabled: uploading,
    });

    return (
      <Box sx={{ mb: 3 }}>
        <Box
          {...getRootProps()}
          sx={{
            border: "2px dashed",
            borderColor: "neutral.300",
            borderRadius: "sm",
            p: 3,
            textAlign: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              borderColor: "primary.500",
              backgroundColor: "primary.50",
            },
          }}
        >
          <input {...getInputProps()} />
          <Typography level="body-sm" color="neutral">
            Drag and drop files here, or click to select files
          </Typography>
        </Box>

        {(existingFiles.length > 0 || files.length > 0) && (
          <List sx={{ mt: 2 }}>
            {existingFiles.map((file) => (
              <ListItem key={file.id}>
                <ListItemDecorator>
                  <DescriptionIcon />
                </ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-sm">{file.fileName}</Typography>
                  <Typography level="body-xs">
                    {(file.fileSize / 1024).toFixed(1)} KB
                  </Typography>
                </ListItemContent>
                {deletingId === file.id ? (
                  <Chip size="sm" variant="soft" color="danger">
                    Removing...
                  </Chip>
                ) : (
                  <Button
                    size="sm"
                    variant="plain"
                    color="danger"
                    onClick={() => deleteTemplate(file.id)}
                    disabled={deletingId !== null}
                  >
                    Remove
                  </Button>
                )}
              </ListItem>
            ))}
            {files.map((file, index) => (
              <ListItem key={`new-${index}`}>
                <ListItemDecorator>
                  <DescriptionIcon />
                </ListItemDecorator>
                <ListItemContent>
                  <Typography level="body-sm">{file.name}</Typography>
                  <Typography level="body-xs">
                    {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                </ListItemContent>
                {uploading && (
                  <Chip size="sm" variant="soft" color="primary">
                    Uploading...
                  </Chip>
                )}
              </ListItem>
            ))}
          </List>
        )}

        {error && (
          <Typography level="body-sm" color="danger" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <>
      <Stack spacing={3}>
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography
              level="h3"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              1.{" "}
              <DescriptionIcon
                sx={{ fontSize: "1.5rem", color: "primary.500" }}
              />
              Example Documents
            </Typography>
            <Tooltip
              title="Upload up to 3 high-quality documents that represent the ideal output. These examples will guide the AI in generating content with similar structure, style, and quality."
              placement="top"
            >
              <IconButton size="sm" variant="plain">
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <FileUploadSection
            number="1"
            title="Example Documents"
            icon={DescriptionIcon}
            tooltip="Upload up to 3 high-quality documents that represent the ideal output. These examples will guide the AI in generating content with similar structure, style, and quality."
            templateType="Example"
            maxFiles={3}
            maxSize={10 * 1024 * 1024}
            accept={{
              "application/pdf": [".pdf"],
              "application/msword": [".doc", ".docx"],
            }}
          />
        </Box>

        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography
              level="h3"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              2.{" "}
              <MenuBookIcon sx={{ fontSize: "1.5rem", color: "primary.500" }} />
              Guidance Documents
            </Typography>
            <Tooltip
              title="Upload up to 3 documents containing specific instructions, requirements, or guidelines. The AI will follow these instructions when generating content, ensuring compliance with your standards and requirements."
              placement="top"
            >
              <IconButton size="sm" variant="plain">
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <FileUploadSection
            number="2"
            title="Guidance Documents"
            icon={MenuBookIcon}
            tooltip="Upload up to 3 documents containing specific instructions, requirements, or guidelines. The AI will follow these instructions when generating content, ensuring compliance with your standards and requirements."
            templateType="Guidance"
            maxFiles={3}
            maxSize={10 * 1024 * 1024}
            accept={{
              "application/pdf": [".pdf"],
              "application/msword": [".doc", ".docx"],
            }}
          />
        </Box>

        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography
              level="h3"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              3.{" "}
              <ViewQuiltIcon
                sx={{ fontSize: "1.5rem", color: "primary.500" }}
              />
              Formatting Template
            </Typography>
            <IconButton
              size="sm"
              variant="plain"
              onClick={() => setStyleGuideOpen(true)}
              aria-label="Info om mallchecklista"
              sx={{ fontSize: "1.1rem" }}
            >
              <InfoOutlinedIcon />
            </IconButton>
          </Stack>
          <FileUploadSection
            number="3"
            title="Formatting Template"
            icon={ViewQuiltIcon}
            tooltip="Upload a single Word document (.doc/.docx) that defines the styles and formatting to be used in the final output. The generated document will inherit all styles, headers, footers, and formatting from this template."
            templateType="Formatting"
            maxFiles={1}
            maxSize={5 * 1024 * 1024}
            accept={{ "application/msword": [".doc", ".docx"] }}
          />
        </Box>
      </Stack>

      <Modal
        open={errorModal.isOpen}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
      >
        <ModalDialog>
          <DialogTitle>Error</DialogTitle>
          <Stack spacing={2}>
            <Typography>{errorModal.message}</Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button
                variant="solid"
                color="primary"
                onClick={() => setErrorModal({ isOpen: false, message: "" })}
              >
                OK
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>
      <Modal open={styleGuideOpen} onClose={() => setStyleGuideOpen(false)}>
        <ModalDialog>
          <DialogTitle>
            Template checklist for accurate style mapping
          </DialogTitle>
          <Stack spacing={2}>
            <Typography level="body-sm">
              For best results when uploading a Word template, follow this
              guidance:
            </Typography>
            <List sx={{ "--ListItemDecorator-size": "20px" }}>
              <ListItem>
                <ListItemDecorator>•</ListItemDecorator>
                <Typography level="body-sm">
                  Use built-in style names where possible. If you already have
                  “Heading 1/2/3”, “Normal”, “List Bullet”, “List Number”,
                  “Table Grid”, prefer to keep them. The system will recognize
                  these defaults.
                </Typography>
              </ListItem>
              <ListItem>
                <ListItemDecorator>•</ListItemDecorator>
                <Typography level="body-sm">
                  If you create custom styles, give them semantic names. Prefer
                  names like "Heading 1" - "Title", "Body", "Bullet List",
                  "Numbered List", "Table Body" instead of "Style1", "MyStyle",
                  etc.
                </Typography>
              </ListItem>
              <ListItem>
                <ListItemDecorator>•</ListItemDecorator>
                <Typography level="body-sm">
                  Apply the styles in actual content. The system validates
                  against styles used in the template. If a style isn’t used in
                  the document body, it may be discarded.
                </Typography>
              </ListItem>
              <ListItem>
                <ListItemDecorator>•</ListItemDecorator>
                <Typography level="body-sm">
                  Include a short “content sample page” in the template. Add one
                  example for each key block:
                  <br />
                  Heading 1–5
                  <br />
                  Body paragraph
                  <br />
                  Bullet list + numbered list
                  <br />
                  Table (with header and body rows)
                </Typography>
              </ListItem>
              <ListItem>
                <ListItemDecorator>•</ListItemDecorator>
                <Typography level="body-sm">
                  Ensure list styles are proper list styles. Use Word’s list
                  formatting (not manual “•” characters).
                </Typography>
              </ListItem>
              <ListItem>
                <ListItemDecorator>•</ListItemDecorator>
                <Typography level="body-sm">
                  Keep one clear style per semantic role. If you have multiple
                  “Heading 1” variants, it’s harder for the system to pick the
                  right one.
                </Typography>
              </ListItem>
              <ListItem>
                <ListItemDecorator>•</ListItemDecorator>
                <Typography level="body-sm">
                  Avoid ambiguous naming. If you have “Title Big”, “Title
                  Bigger”, the system won’t know which is “heading1” vs
                  “heading2”.
                </Typography>
              </ListItem>
              <ListItem>
                <ListItemDecorator>•</ListItemDecorator>
                <Typography level="body-sm">
                  Make table styles obvious. Apply a table style (or name it
                  “Table …”) and ensure the table is part of the sample content.
                </Typography>
              </ListItem>
            </List>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="solid" onClick={() => setStyleGuideOpen(false)}>
                Close
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default TemplatesTab;
